/**
 * @typedef {import('src/type-defs/five9Types').Five9Call} Five9Call
 * @typedef {import('redux').Dispatch} ReduxDispatch
 */
import { upsertCall, setActiveCall, clearCall } from 'src/actions/five9Actions';
import logger from './five9Logger';
import extractCall from './extractCall';
import { emitFive9CallLifecycleEvent } from './five9CallLifecycleEvents';

/** @type {any} */
let cachedApi = null;

/** @type {Set<(api: any|null) => void>} */
const availabilityListeners = new Set();

const setCachedApi = (next) => {
  if (cachedApi === next) return;
  cachedApi = next;
  availabilityListeners.forEach((listener) => {
    try {
      listener(next);
    } catch (_) {
      // Ignore listener failures.
    }
  });
};

/** @type {(() => void)|null} */
let activeTeardown = null;

/** @type {number} */
let subscriptionGeneration = 0;

/**
 * @param {string} label
 * @param {object|null|undefined} target
 */
const dumpSurface = (label, target) => {
  try {
    const proto = target ? (Object.getPrototypeOf(target) || {}) : {};
    const keys = Array.from(new Set([
      ...(target ? Object.keys(target) : []),
      ...Object.getOwnPropertyNames(proto)
    ])).filter((k) => k !== 'constructor');
    const typeMap = {};

    keys.forEach((k) => {
      try {
        typeMap[k] = typeof target[k];
      } catch (_) {
        typeMap[k] = '?';
      }
    });

    logger.info(label, { keys, types: typeMap });
  } catch (err) {
    logger.warn(`${label}.dumpFailed`, { message: err?.message });
  }
};

/**
 * @returns {any|null}
 */
const acquireInteractionApi = () => {
  const sdk = typeof window !== 'undefined' ? window?.Five9?.CrmSdk : null;
  if (!sdk) {
    logger.warn('interaction.unsupported', { reason: 'no window.Five9.CrmSdk' });
    return null;
  }

  dumpSurface('interaction.sdkSurface', sdk);
  const candidates = [
    { via: 'interactionApi()', get: () => (typeof sdk.interactionApi === 'function' ? sdk.interactionApi() : null) },
    { via: 'interactionApi', get: () => (sdk.interactionApi && typeof sdk.interactionApi === 'object' ? sdk.interactionApi : null) },
    { via: 'getInteractionApi()', get: () => (typeof sdk.getInteractionApi === 'function' ? sdk.getInteractionApi() : null) }
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    try {
      const api = candidates[i].get();
      if (api) {
        logger.info('interaction.acquired', { via: candidates[i].via });
        return api;
      }
    } catch (err) {
      logger.warn('interaction.acquireAttemptFailed', { via: candidates[i].via, message: err?.message });
    }
  }

  logger.warn('interaction.unsupported', { reason: 'no interactionApi handle' });
  return null;
};

/**
 * @returns {any|null}
 */
export function getInteractionApi() {
  return cachedApi;
}

/**
 * @param {(api: any|null) => void} listener
 * @returns {() => void}
 */
export function subscribeToInteractionApiAvailability(listener) {
  availabilityListeners.add(listener);
  return () => availabilityListeners.delete(listener);
}

/**
 * @param {any} api
 * @returns {Array<{ key: string, value: string }>}
 */
const listWsEvents = (api) => {
  const events = api && api.Events;
  if (!events || typeof events !== 'object') return [];

  const entries = [];
  Object.keys(events).forEach((key) => {
    const value = events[key];
    if (value == null) return;
    entries.push({ key, value: String(value) });
  });

  logger.info('interaction.Events', { entries });
  return entries;
};

/**
 * @param {ReduxDispatch} reduxDispatch
 * @param {{ publishCallLifecycle?: boolean }} [options]
 * @returns {() => void}
 */
export default function subscribeToInteractionApi(reduxDispatch, { publishCallLifecycle = true } = {}) {
  if (activeTeardown) {
    const previous = activeTeardown;
    activeTeardown = null;
    logger.warn('interaction.staleSubscriptionReplaced', {
      note: 'a previous interactionApi subscription was still live; retiring it first'
    });
    try {
      previous();
    } catch (_) {
      // Ignore teardown failures.
    }
  }

  const api = acquireInteractionApi();
  if (!api) return () => {};

  setCachedApi(api);
  dumpSurface('interaction.apiSurface', api);

  subscriptionGeneration += 1;
  const generation = subscriptionGeneration;
  const isStale = () => generation !== subscriptionGeneration;
  let hasLoggedStaleEvent = false;

  try {
    if (typeof api.getMetadata === 'function') {
      const meta = api.getMetadata();
      Promise.resolve(meta)
        .then((m) => logger.info('interaction.metadata', { meta: m }))
        .catch((err) => logger.warn('interaction.metadataFailed', { message: err?.message }));
    }
  } catch (err) {
    logger.warn('interaction.metadataFailed', { message: err?.message });
  }

  const unsubscribers = [];
  const listenerObj = {
    onError: (err) => logger.warn('interaction.subscribe.error', { message: err?.message, err })
  };
  const events = listWsEvents(api);

  events.forEach(({ key }) => {
    if (key === 'interactionOpened') return;

    const perEventHandler = (payload) => {
      if (isStale()) {
        if (!hasLoggedStaleEvent) {
          hasLoggedStaleEvent = true;
          logger.debug('interaction.staleEventIgnored', { event: key, generation });
        }
        return;
      }

      logger.event(`interaction.${key}`, payload);
      const isCallLifecycle = key.startsWith('call');
      if (!isCallLifecycle) return;

      if (publishCallLifecycle) emitFive9CallLifecycleEvent(`interaction.${key}`, payload);
      const call = extractCall(payload);
      if (!call) return;

      reduxDispatch(upsertCall(key === 'callEnded' ? { ...call, endedAt: Date.now() } : call));
      reduxDispatch(setActiveCall(call.callSid));

      if (/(Finished|Rejected)$/.test(key)) {
        reduxDispatch(clearCall(call.callSid));
      }
    };

    listenerObj[key] = perEventHandler;
  });

  let listenerSubscribed = false;
  try {
    const un = api.subscribe(listenerObj);
    listenerSubscribed = true;
    logger.event('interaction.subscribed', {
      via: 'subscribe(listenerObject)',
      returned: typeof un,
      keys: Object.keys(listenerObj)
    });

    unsubscribers.push(() => {
      if (typeof un === 'function') {
        try {
          un();
        } catch (_) {
          // Ignore teardown failures.
        }
      }
      try {
        api.unsubscribe?.(listenerObj);
      } catch (_) {
        // Ignore teardown failures.
      }
    });
  } catch (err) {
    logger.warn('interaction.subscribeFailed', {
      via: 'subscribe(listenerObject)',
      message: err?.message
    });
  }

  let perEventCount = 0;
  if (typeof api.subscribeWsEvent === 'function' && events.length) {
    events.forEach(({ key, value }) => {
      const eventName = value;
      const handler = (payload) => {
        if (isStale()) return;
        logger.event(`interaction.ws.${key}`, {
          eventName,
          data: payload
        });
      };

      try {
        const un = api.subscribeWsEvent(eventName, handler);
        perEventCount += 1;
        unsubscribers.push(typeof un === 'function'
          ? un
          : () => {
            try {
              api.unsubscribeWsEvent?.(eventName, handler);
            } catch (_) {
              // Ignore teardown failures.
            }
          });
      } catch (err) {
        logger.warn('interaction.subscribeFailed', {
          via: `subscribeWsEvent('${eventName}', handler)`,
          message: err?.message
        });
      }
    });
  }

  if (!listenerSubscribed && perEventCount === 0) {
    logger.error(
      'interaction.noSubscribeMethod',
      {},
      'interactionApi has no working subscribe/subscribeWsEvent path'
    );
  } else {
    logger.event('interaction.ready', {
      listenerObject: listenerSubscribed,
      perEventCount,
      totalEvents: events.length
    });
  }

  const teardown = () => {
    if (generation === subscriptionGeneration) subscriptionGeneration += 1;
    if (activeTeardown === teardown) activeTeardown = null;
    unsubscribers.forEach((fn) => {
      try {
        fn();
      } catch (_) {
        // Ignore teardown failures.
      }
    });
    if (cachedApi === api) setCachedApi(null);
  };

  activeTeardown = teardown;
  return teardown;
}
